"use client";
import { FC, useState, useEffect } from "react";
import BFLLink from "@repo/shared-ui/BFLLink";
import Conditional from "@repo/shared-ui/Conditional";
import MenuToggle from "./MenuToogle";
import {
  TopNavItem,
  QuickLink,
  TertiaryDetail,
} from "../../interfaces/IHeader.interface";

interface LeftMenuProps {
  navItems?: TopNavItem[];
  bottomnavigationmobile?: any;
}

// Render quickLinks section below tertiaryNav
const renderQuickLinks = (
  quickLinks: QuickLink[],
  quickLinksHeading: string,
) => {
  if (!quickLinks || quickLinks.length === 0) return null;
  return (
    <>
      {quickLinksHeading && (
        <h3 className="mb-[18px] text-sm font-[500] uppercase text-blue-400">
          {quickLinksHeading}
        </h3>
      )}
      <ul className="flex flex-wrap">
        {quickLinks.map((ql: any, idx: number) => (
          <li
            className="mb-2 mr-2"
            key={ql.quickLinksCtaLink || ql.quickLinksTitle || idx}
          >
            <BFLLink
              href={ql.quickLinksCtaLink}
              openInNewTab={ql.quickLinksCtaNewTab === "true"}
              className="mb-2 block cursor-pointer rounded-[12px] bg-[#1a2734] px-2 py-1 text-xs"
            >
              {ql.quickLinksTitle}
            </BFLLink>
          </li>
        ))}
      </ul>
    </>
  );
};

const renderTertiaryDetails = (tertiary: any) => {
  const tertiaryDetails =
    tertiary?.children?.find(
      (child: any) => child.nodeName === "tertiaryDetails",
    )?.children || [];
  const columnTitle = tertiary?.columnTitle;

  return (
    <>
      {columnTitle && (
        <h2 className="mb-[18px] text-sm font-[500] uppercase text-blue-400">
          {columnTitle}
        </h2>
      )}
      {tertiaryDetails.map((td: any) => (
        <li
          className="whitespace-normal"
          key={td.tertiaryCtaLink || td.tertiaryLabel}
        >
          <BFLLink
            href={td.tertiaryCtaLink}
            openInNewTab={td.tertiaryCtaInNewTab === "true"}
            className="mb-4 block cursor-pointer text-sm font-normal transition-all duration-200 hover:font-[600]"
          >
            {td.tertiaryLabel}
          </BFLLink>
        </li>
      ))}
    </>
  );
};

const renderTertiaryNavWithQuickLinks = (
  tertiaryNav: TertiaryDetail[],
  quickLinks: QuickLink[],
  quickLinksHeading: string,
  parentNavTitle?: string, // Add parent nav title parameter
) => {
  const chunked = [];
  for (let i = 0; i < tertiaryNav.length; i += 2) {
    chunked.push(tertiaryNav.slice(i, i + 2));
  }

  // Don't add fixed height for Services menu
  const isServicesMenu = parentNavTitle === "Services";

  return (
    <ul
      className={`no-scrollbar absolute left-[280px] top-0 hidden ${!isServicesMenu ? "h-[781px] w-[540px] max-w-[540px]" : "h-[420px] w-[280px] max-w-[280px]"} max-h-[82vh] min-w-[280px] overflow-y-auto bg-blue-900 px-4 py-[10px] group-hover/submenu:block`}
    >
      {chunked.map((pair) => {
        const rowKey = pair
          .map((tertiary: any) => tertiary.id || tertiary.nodeName)
          .join("-");
        return (
          <li
            key={rowKey}
            className={`m-0 ${!isServicesMenu ? "flex" : ""} list-none flex-wrap p-0`}
          >
            {pair.map((tertiary: any, tIdx: number) => (
              <div
                key={tertiary.id || tertiary.nodeName || tIdx}
                className={`mb-10 ${!isServicesMenu ? "w-1/2" : ""}`}
              >
                <ul className="ml-2 space-y-1">
                  {renderTertiaryDetails(tertiary)}
                </ul>
              </div>
            ))}
          </li>
        );
      })}

      {quickLinks.length > 0 && renderQuickLinks(quickLinks, quickLinksHeading)}
    </ul>
  );
};

const renderSecondaryNavDetails = (
  secondaryNavDetails: any[],
  parentNavTitle?: string,
) =>
  secondaryNavDetails.map((detail: any, secIdx: number) => {
    const tertiaryNav =
      detail?.xfContent?.children?.[0]?.children?.find(
        (child: any) => child.nodeName === "tertiaryLinks",
      )?.children || [];

    const quickLinks =
      detail?.xfContent?.children?.[0]?.children?.find(
        (child: any) => child.nodeName === "quickLinks",
      )?.children || [];

    const quickLinksHeading =
      detail?.xfContent?.children?.[0]?.quickLinksHeading;

    return (
      <li
        className="group/submenu hover:bg-blue-900"
        key={detail.id || detail.secondaryNavLabel || secIdx}
      >
        <BFLLink
          href={detail?.ctaLink}
          className="block cursor-pointer px-4 py-[10px]"
        >
          {detail.secondaryNavLabel}
        </BFLLink>

        <Conditional
          if={!!(detail?.primaryCtaText || detail?.secondaryCtaText)}
        >
          <span className="hidden px-4 py-[10px] group-hover/submenu:block">
            {detail?.primaryCtaText && (
              <BFLLink
                className="from-link-primary mr-2 cursor-pointer rounded-[25px] bg-gradient-to-r to-[#ff8900] px-3 py-1 text-xs"
                href={detail?.primaryCtaLink}
              >
                {detail.primaryCtaText}
              </BFLLink>
            )}
            {detail?.secondaryCtaText && (
              <BFLLink
                className="text-link-primary cursor-pointer text-sm font-[500]"
                href={detail?.secondaryCtaLink}
              >
                {detail.secondaryCtaText}
              </BFLLink>
            )}
          </span>
        </Conditional>

        {(tertiaryNav.length > 0 || quickLinks.length > 0) &&
          renderTertiaryNavWithQuickLinks(
            tertiaryNav,
            quickLinks,
            quickLinksHeading,
            parentNavTitle,
          )}
      </li>
    );
  });

const LeftMenu: FC<LeftMenuProps> = ({
  navItems = [],
  bottomnavigationmobile,
}) => {
  const [showSecondaryNav, setShowSecondaryNav] = useState(true);
  const [submenuHovered, setSubmenuHovered] = useState(false);
  // Track if a submenu (not main menu) is hovered
  const [subMenuActive, setSubMenuActive] = useState(false);

  const HEADER_HEIGHT = 124;

  // Prevent background scroll only when submenu (not main menu) is hovered
  useEffect(() => {
    if (showSecondaryNav && subMenuActive) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }

    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [showSecondaryNav, subMenuActive]);

  return (
    <>
      {/* Overlay */}
      {showSecondaryNav && submenuHovered && (
        <div
          className="fixed bottom-0 left-0 right-0 z-10 bg-[rgba(0,0,0,0.7)]"
          style={{ top: `${HEADER_HEIGHT}px` }}
        />
      )}

      <div className="relative z-20 flex">
        <MenuToggle bottomnavigationmobile={bottomnavigationmobile} />
        <ul className="flex" data-testid="main-nav-list">
          {navItems.length > 0 ? (
            navItems.map((item: any) => {
              const secondaryNavDetails =
                item?.xfContent?.children?.[0]?.children?.find(
                  (child: any) => child.nodeName === "secondaryNavDetails",
                )?.children || [];

              return (
                <li
                  className="group relative flex items-center gap-1 py-[10px] leading-[20px] hover:bg-blue-900 md:px-[1px] md:text-xs lg:px-[6px] lg:text-sm"
                  key={item.id || item.nodeName || item.topNavTitle}
                  onMouseEnter={() => setSubmenuHovered(true)}
                  onMouseLeave={() => setSubmenuHovered(false)}
                >
                  {item.topNavTitle || item.nodeName}
                  <i className="icomoon text-sm before:content-['\e909']"></i>
                  {showSecondaryNav &&
                    submenuHovered &&
                    secondaryNavDetails.length > 0 && (
                      <ul className="absolute top-full z-20 hidden min-w-[280px] bg-blue-500 group-hover:block">
                        <div
                          className={`no-scrollbar content-visibility-hidden group-hover:content-visibility-visible ${item.topNavTitle === "Investments" ? "min-h-[82vh]" : ""} max-h-[82vh] overflow-y-auto pb-[20px] [contain-intrinsic-size:100px]`}
                          onMouseEnter={() => setSubMenuActive(true)}
                          onMouseLeave={() => setSubMenuActive(false)}
                        >
                          {renderSecondaryNavDetails(
                            secondaryNavDetails,
                            item.topNavTitle,
                          )}
                        </div>
                      </ul>
                    )}
                </li>
              );
            })
          ) : (
            <li className="px-3 py-[10px] text-xs lg:text-sm">
              No navigation items available
            </li>
          )}
        </ul>
      </div>
    </>
  );
};

export default LeftMenu;
